import { FESTIVALS } from "./festivals";
import type { FestivalConfig, Genre, Region } from "./types";
import { festivalListings } from "./listings";
import { typicalWindowForYear } from "./windows";

export type CapacityBucket = "under-10k" | "10k-50k" | "50k-200k" | "over-200k";

export const MONTH_NAMES = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
] as const;

export const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export type MonthName = (typeof MONTH_NAMES)[number];

export function toMonthSlug(year: number, month1to12: number): string {
  return `${MONTH_NAMES[month1to12 - 1]}-${year}`;
}

export function fromMonthSlug(
  slug: string
): { year: number; month: number; label: string } | null {
  const m = slug.match(/^([a-z]+)-(\d{4})$/);
  if (!m) return null;
  const monthIdx = MONTH_NAMES.indexOf(m[1] as MonthName);
  if (monthIdx < 0) return null;
  const year = parseInt(m[2], 10);
  if (!Number.isFinite(year)) return null;
  return { year, month: monthIdx + 1, label: `${MONTH_LABELS[monthIdx]} ${year}` };
}

export function rollingMonthSlugs(now: Date, count = 12): string[] {
  const out: string[] = [];
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth() + 1;
  for (let i = 0; i < count; i++) {
    const m = ((month - 1 + i) % 12) + 1;
    const y = year + Math.floor((month - 1 + i) / 12);
    out.push(toMonthSlug(y, m));
  }
  return out;
}

type FestivalRange = { start: Date; end: Date; isConfirmed: boolean };

export function eventRangeForYear(
  festival: FestivalConfig,
  year: number
): FestivalRange {
  const confirmed = festival.confirmedDates?.find((d) => d.year === year);
  if (confirmed) {
    return {
      start: new Date(`${confirmed.start}T00:00:00Z`),
      end: new Date(`${confirmed.end}T00:00:00Z`),
      isConfirmed: true,
    };
  }
  const { start, end } = typicalWindowForYear(festival, year);
  return { start, end, isConfirmed: false };
}

export type FestivalInMonth = {
  festival: FestivalConfig;
  year: number;
  start: Date;
  end: Date;
  clippedStartDay: number;
  clippedEndDay: number;
  isConfirmed: boolean;
  durationDays: number;
  overflowsStart: boolean;
  overflowsEnd: boolean;
};

export function festivalsInMonth(
  year: number,
  month1to12: number
): FestivalInMonth[] {
  const monthStart = new Date(Date.UTC(year, month1to12 - 1, 1));
  const monthEnd = new Date(Date.UTC(year, month1to12, 0));

  const out: FestivalInMonth[] = [];

  const listings = festivalListings(FESTIVALS, monthStart.toISOString().slice(0, 10), monthEnd.toISOString().slice(0, 10));
  for (const listing of listings) {
    for (const window of listing.windows) {
      const start = new Date(`${window.start}T00:00:00Z`);
      const end = new Date(`${window.end}T00:00:00Z`);
      const clippedStart = start < monthStart ? monthStart : start;
      const clippedEnd = end > monthEnd ? monthEnd : end;
      out.push({
        festival: listing.festival, year: listing.year, start, end,
        clippedStartDay: clippedStart.getUTCDate(), clippedEndDay: clippedEnd.getUTCDate(),
        isConfirmed: listing.confirmed,
        durationDays: Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1,
        overflowsStart: start < monthStart, overflowsEnd: end > monthEnd,
      });
    }
  }

  out.sort((a, b) => {
    if (a.clippedStartDay !== b.clippedStartDay)
      return a.clippedStartDay - b.clippedStartDay;
    if (a.durationDays !== b.durationDays) return b.durationDays - a.durationDays;
    return a.festival.name.localeCompare(b.festival.name);
  });

  return out;
}

export function daysInMonth(year: number, month1to12: number): number {
  return new Date(Date.UTC(year, month1to12, 0)).getUTCDate();
}

export function capacityBucket(capacity?: number): CapacityBucket | null {
  if (capacity == null) return null;
  if (capacity < 10_000) return "under-10k";
  if (capacity < 50_000) return "10k-50k";
  if (capacity < 200_000) return "50k-200k";
  return "over-200k";
}

export const CAPACITY_BUCKET_LABEL: Record<CapacityBucket, string> = {
  "under-10k": "Under 10k",
  "10k-50k": "10k to 50k",
  "50k-200k": "50k to 200k",
  "over-200k": "200k+",
};

export const CAPACITY_BUCKET_ORDER: CapacityBucket[] = [
  "under-10k",
  "10k-50k",
  "50k-200k",
  "over-200k",
];

export const REGION_COLOUR: Record<Region, string> = {
  uk: "bg-gold",
  europe: "bg-sage",
  "north-america": "bg-terracotta",
  anz: "bg-navy-mid",
};

export const REGION_LABEL: Record<Region, string> = {
  uk: "UK",
  europe: "Europe",
  "north-america": "North America",
  anz: "Australia & NZ",
};

export const REGION_ORDER: Region[] = ["uk", "europe", "north-america", "anz"];

export const GENRE_LABEL: Record<Genre, string> = {
  electronic: "Electronic",
  dance: "Dance",
  indie: "Indie",
  rock: "Rock",
  metal: "Metal",
  pop: "Pop",
  "hip-hop": "Hip-hop",
  folk: "Folk",
  country: "Country",
  world: "World music",
  "multi-genre": "Multi-genre",
  boutique: "Boutique",
};

export const GENRE_ORDER: Genre[] = [
  "multi-genre",
  "electronic",
  "dance",
  "indie",
  "rock",
  "metal",
  "hip-hop",
  "pop",
  "folk",
  "country",
  "world",
  "boutique",
];

export function hasWork(f: FestivalConfig): boolean {
  return Boolean(f.relatedGuideSlug);
}

export function formatDayRange(f: FestivalInMonth): string {
  const fmt = (d: Date) =>
    d.toLocaleString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  const label = f.isConfirmed
    ? `${fmt(f.start)} to ${fmt(f.end)}`
    : `typical: ${fmt(f.start)} to ${fmt(f.end)}`;
  return label;
}

// ── Multi-month window helpers ───────────────────────────────────────────

export type FestivalInWindow = {
  festival: FestivalConfig;
  year: number;
  start: Date;
  end: Date;
  startPct: number;
  widthPct: number;
  isConfirmed: boolean;
  durationDays: number;
};

export function festivalsInWindow(
  startYear: number,
  startMonth: number,
  months: number,
): FestivalInWindow[] {
  const windowStart = new Date(Date.UTC(startYear, startMonth - 1, 1));
  const windowEnd = new Date(Date.UTC(startYear, startMonth - 1 + months, 0, 23, 59, 59));
  const span = windowEnd.getTime() - windowStart.getTime();

  const out: FestivalInWindow[] = [];
  const listings = festivalListings(FESTIVALS, windowStart.toISOString().slice(0, 10), windowEnd.toISOString().slice(0, 10));
  for (const listing of listings) {
    for (const window of listing.windows) {
      const start = new Date(`${window.start}T00:00:00Z`);
      const end = new Date(`${window.end}T00:00:00Z`);
      const clippedStart = Math.max(start.getTime(), windowStart.getTime());
      const clippedEnd = Math.min(end.getTime() + 86_400_000, windowEnd.getTime());
      out.push({
        festival: listing.festival, year: listing.year, start, end,
        startPct: ((clippedStart - windowStart.getTime()) / span) * 100,
        widthPct: Math.max(((clippedEnd - clippedStart) / span) * 100, 0.8),
        isConfirmed: listing.confirmed,
        durationDays: Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1,
      });
    }
  }

  out.sort((a, b) => a.start.getTime() - b.start.getTime() || a.festival.name.localeCompare(b.festival.name));
  return out;
}

export type MonthSpan = { year: number; month: number; label: string; startPct: number; widthPct: number };

export function monthSpansForWindow(
  startYear: number,
  startMonth: number,
  months: number,
): MonthSpan[] {
  const windowStart = new Date(Date.UTC(startYear, startMonth - 1, 1));
  const windowEnd = new Date(Date.UTC(startYear, startMonth - 1 + months, 0, 23, 59, 59));
  const span = windowEnd.getTime() - windowStart.getTime();

  const out: MonthSpan[] = [];
  for (let i = 0; i < months; i++) {
    const total = (startYear * 12) + (startMonth - 1) + i;
    const y = Math.floor(total / 12);
    const m = (total % 12) + 1;
    const mStart = new Date(Date.UTC(y, m - 1, 1));
    const mEnd = new Date(Date.UTC(y, m, 0, 23, 59, 59));
    const clippedStart = mStart < windowStart ? windowStart : mStart;
    const clippedEnd = mEnd > windowEnd ? windowEnd : mEnd;
    const startPct = ((clippedStart.getTime() - windowStart.getTime()) / span) * 100;
    const widthPct = ((clippedEnd.getTime() - clippedStart.getTime()) / span) * 100;
    out.push({ year: y, month: m, label: `${MONTH_LABELS[m - 1]}`, startPct, widthPct });
  }
  return out;
}

export function weekLinesForWindow(
  startYear: number,
  startMonth: number,
  months: number,
): number[] {
  const windowStart = new Date(Date.UTC(startYear, startMonth - 1, 1));
  const windowEnd = new Date(Date.UTC(startYear, startMonth - 1 + months, 0, 23, 59, 59));
  const span = windowEnd.getTime() - windowStart.getTime();

  const dayMs = 86_400_000;
  const startDay = windowStart.getUTCDay();
  const daysToMonday = startDay === 0 ? 1 : (8 - startDay) % 7;
  const firstMonday = new Date(windowStart.getTime() + daysToMonday * dayMs);

  const lines: number[] = [];
  const cursor = new Date(firstMonday);
  while (cursor <= windowEnd) {
    const pct = ((cursor.getTime() - windowStart.getTime()) / span) * 100;
    lines.push(pct);
    cursor.setTime(cursor.getTime() + 7 * dayMs);
  }
  return lines;
}

export function peakSeasonRange(
  startYear: number,
  startMonth: number,
  months: number,
): { startPct: number; widthPct: number } | null {
  const windowStart = new Date(Date.UTC(startYear, startMonth - 1, 1));
  const windowEnd = new Date(Date.UTC(startYear, startMonth - 1 + months, 0, 23, 59, 59));
  const span = windowEnd.getTime() - windowStart.getTime();

  const years = new Set([startYear]);
  for (let i = 0; i < months; i++) {
    const total = (startYear * 12) + (startMonth - 1) + i;
    years.add(Math.floor(total / 12));
  }

  let peakStart: Date | null = null;
  let peakEnd: Date | null = null;
  for (const y of years) {
    const s = new Date(Date.UTC(y, 5, 1));
    const e = new Date(Date.UTC(y, 8, 30, 23, 59, 59));
    if (e < windowStart || s > windowEnd) continue;
    const cs = s < windowStart ? windowStart : s;
    const ce = e > windowEnd ? windowEnd : e;
    if (!peakStart || cs < peakStart) peakStart = cs;
    if (!peakEnd || ce > peakEnd) peakEnd = ce;
  }

  if (!peakStart || !peakEnd) return null;
  const startPct = ((peakStart.getTime() - windowStart.getTime()) / span) * 100;
  const widthPct = ((peakEnd.getTime() - peakStart.getTime()) / span) * 100;
  if (widthPct < 1) return null;
  return { startPct, widthPct };
}
